// @ts-nocheck

import {
  CoreBatchAction,
  type NodeItem,
  ProjectActions,
  selectNodeBlocks,
  selectTabNodes,
} from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { AIActions } from '@store/reducers/ai'
import { artBoardActions } from '@store/reducers/artboard'
import {
  commitProjectRequest,
  rollbackToBackupSnapshot,
  saveBackupSnapshot,
} from '@storeOld/actionCreators'
import { useDispatch, useStore } from 'react-redux'

import { DEFAULT_PROJECT_NAME } from '@/constants/constants'
import { AI_ATTEMPTS, VITE_AI_HOST } from '@/constants/env'
import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import {
  clearAiNotifications,
  notifyAiGeneratingFinished,
  notifyAiInProgress,
} from '@/hooks/ai/actionCreators'
import useIndexer from '@/hooks/useIndexer'
import type { AppState } from '@/types'
import { parseCsv, postEventStreamV2 } from '@/utils/streamsV2'

const ActionsMap = {
  p: ProjectActions.nodes.updateTitle,
  b: ProjectActions.blocks.updateTitle,
  s: ProjectActions.sections.updateTitle,
}

export const LANGUAGES = [
  'English',
  'Korean',
  'Chinese',
  'Japanese',
  'Spanish',
  'Russian',
  'French',
  'Portuguese',
  'German',
  'Italian',
] as const

export type TranslationLanguage = (typeof LANGUAGES)[number]

// The sitemap is read when a language is picked, not on every render of the
// action bar that hosts the menu.
export const useTranslateHandler = (): ((
  lang: TranslationLanguage
) => void) => {
  const dispatch = useDispatch()
  const store = useStore<AppState>()
  const indexer = useIndexer<string>()

  return async (lang: TranslationLanguage) => {
    const state = store.getState()
    const projectTitle = migrationProjectSelector.getProjectTitle(state)
    const activeTab = migrationProjectSelector.getActiveOrFirstTabId(state)

    const pages: NodeItem[] = selectTabNodes(activeTab)({
      project: state.manager.project,
      relations: state.relations,
    }).reduce((acc, node) => {
      const blocks = selectNodeBlocks(node.id)({
        project: state.manager.project,
        relations: state.relations,
      })

      return [
        ...acc,
        {
          index: indexer.addAndGetIndex(node.id),
          name: node.title,
          blocks: blocks.map((block) => ({
            index: indexer.addAndGetIndex(block.id),
            name: block.title,
          })),
        },
      ]
    }, [])

    let abortController: (() => void) | null = null

    const cleanup = () => {
      if (abortController) {
        window.removeEventListener('CancelAiGeneration', handleCancel)
        abortController = null
      }
    }

    const handleCancel = () => {
      dispatch(clearAiNotifications())

      if (abortController) {
        abortController()
      }
    }

    sendGtmEvent(GtmTriggerName.AI_PROJECT_TRANSLATE)

    // @todo review this
    const projectName =
      projectTitle === DEFAULT_PROJECT_NAME || !projectTitle
        ? undefined
        : projectTitle

    const [stream, getAbortController] = postEventStreamV2(
      `${VITE_AI_HOST}/v2/translate-project`,
      {
        projectName,
        lang,
        pages,
      },
      { attempts: AI_ATTEMPTS }
    )

    abortController = getAbortController

    window.addEventListener('CancelAiGeneration', handleCancel)

    dispatch(artBoardActions.setAiGeneratingStatus({ status: true }))
    dispatch(AIActions.setGenerating(true))

    dispatch(saveBackupSnapshot())
    dispatch(
      notifyAiInProgress(() => {
        getAbortController()
        dispatch(rollbackToBackupSnapshot())
        dispatch(clearAiNotifications())
        dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      }, 'Cancel')
    )

    // @ts-expect-error
    const actionsBatch = []
    parseCsv(stream).on('row', ({ value }) => {
      const [type, groupOrIdIndex, name] = value

      const id = indexer.getByIndex(Number(groupOrIdIndex))

      const actionCreator = ActionsMap[type]
      if (!actionCreator) {
        return
      }

      const action = actionCreator({ id, title: name })

      dispatch(action)
      actionsBatch.push(action)
    })

    stream.on('abort', () => {
      cleanup()
      dispatch(rollbackToBackupSnapshot())
      dispatch(clearAiNotifications())
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.setGenerating(false))
    })

    stream.on('error', () => {
      dispatch(rollbackToBackupSnapshot())
    })

    stream.on('attemptsWasted', () => {
      dispatch(clearAiNotifications())
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.setGenerating(false))
    })

    stream.on('success', () => {
      cleanup()
      dispatch(
        commitProjectRequest(
          CoreBatchAction({
            type: 'ai_generate_project_translate',
            actions: actionsBatch,
          })
        )
      )

      dispatch(clearAiNotifications())
      dispatch(notifyAiGeneratingFinished())
      setTimeout(() => {
        dispatch(clearAiNotifications())
      }, 8000)
      dispatch(artBoardActions.setAiGeneratingStatus({ status: false }))
      dispatch(AIActions.setGenerating(false))

      dispatch(
        AIActions.addMessage({
          message: {
            type: 'assistant',
            text: 'The project has been translated to the selected language. What would you like to do next?',
          },
        })
      )
    })
  }
}
